import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, Mail, Video, Phone, MapPin, Check } from 'lucide-react';
import { schedulingApi } from '@/lib/api';
import { Button } from '@/components/ui/button';

type Booking = {
    id: string;
    event_type_id: string;
    start_time: string;
    end_time: string;
    invitee_name: string;
    invitee_email: string;
    timezone?: string;
    location_type?: string;
    location_details?: string | null;
    meeting_url?: string | null;
    status?: string;
    payment_status?: string | null;
};

type EventType = {
    id: string;
    name: string;
    description?: string | null;
    duration: number;
    slug: string;
    color?: string | null;
    theme?: 'light' | 'dark' | null;
};

export default function BookingSuccess() {
    const { bookingId } = useParams<{ bookingId: string }>();
    const [booking, setBooking] = useState<Booking | null>(null);
    const [eventType, setEventType] = useState<EventType | null>(null);
    const [loading, setLoading] = useState(true);

    const isDark = eventType?.theme === 'dark';
    useEffect(() => {
        if (!isDark) {
            document.documentElement.classList.remove('dark');
            return;
        }
        const wasDark = document.documentElement.classList.contains('dark');
        document.documentElement.classList.add('dark');
        return () => {
            if (!wasDark) document.documentElement.classList.remove('dark');
        };
    }, [isDark]);

    useEffect(() => {
        const load = async () => {
            try {
                const br = await schedulingApi.getBookingById(bookingId!);
                setBooking(br.data as Booking);
                const er = await schedulingApi.getEventTypeById(br.data.event_type_id);
                setEventType(er.data as EventType);
            } catch {
                setBooking(null);
            } finally {
                setLoading(false);
            }
        };
        if (bookingId) load();
    }, [bookingId]);

    const isAwaitingPayment = booking?.payment_status === 'pending';
    const [pollTimedOut, setPollTimedOut] = useState(false);
    const [checkingNow, setCheckingNow] = useState(false);

    const checkPaymentOnce = useCallback(async (): Promise<boolean> => {
        if (!bookingId) return false;
        try {
            const br = await schedulingApi.getBookingById(bookingId);
            if (br?.data?.payment_status && br.data.payment_status !== 'pending') {
                setBooking(br.data as Booking);
                return true;
            }
        } catch { /* transient — caller keeps polling */ }
        return false;
    }, [bookingId]);

    useEffect(() => {
        if (!isAwaitingPayment || !bookingId) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;
        let attempts = 0;
        const FAST_ATTEMPTS = 20;          // ~1 min at FAST_MS
        const FAST_MS = 3000;
        const SLOW_MS = 15000;
        const GIVE_UP_MS = 10 * 60 * 1000; // stop hitting the API after 10 min
        const startedAt = Date.now();

        const tick = async () => {
            if (cancelled) return;
            attempts += 1;
            const resolved = await checkPaymentOnce();
            if (cancelled || resolved) return;          // resolved → stop polling
            if (attempts >= FAST_ATTEMPTS) setPollTimedOut(true);
            if (Date.now() - startedAt >= GIVE_UP_MS) return;
            timer = setTimeout(tick, attempts >= FAST_ATTEMPTS ? SLOW_MS : FAST_MS);
        };

        timer = setTimeout(tick, FAST_MS);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [isAwaitingPayment, bookingId, checkPaymentOnce]);

    const handleCheckNow = useCallback(async () => {
        setCheckingNow(true);
        try { await checkPaymentOnce(); } finally { setCheckingNow(false); }
    }, [checkPaymentOnce]);

    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

    const googleLink = () => {
        if (!booking || !eventType) return '#';
        const p = new URLSearchParams({
            action: 'TEMPLATE',
            text: eventType.name,
            dates: `${fmt(new Date(booking.start_time))}/${fmt(new Date(booking.end_time))}`,
            details: eventType.description || '',
            location: booking.meeting_url || booking.location_details || 'Online',
        });
        return `https://calendar.google.com/calendar/render?${p}`;
    };

    const outlookLink = () => {
        if (!booking || !eventType) return '#';
        const p = new URLSearchParams({
            path: '/calendar/action/compose',
            rru: 'addevent',
            subject: eventType.name,
            startdt: new Date(booking.start_time).toISOString(),
            enddt: new Date(booking.end_time).toISOString(),
            body: eventType.description || '',
            location: booking.meeting_url || booking.location_details || 'Online',
        });
        return `https://outlook.live.com/calendar/0/deeplink/compose?${p}`;
    };

    const downloadICS = () => {
        if (!booking || !eventType) return;
        const ics = [
            'BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
            `DTSTART:${fmt(new Date(booking.start_time))}`,
            `DTEND:${fmt(new Date(booking.end_time))}`,
            `SUMMARY:${eventType.name}`,
            `DESCRIPTION:${eventType.description || ''}`,
            `LOCATION:${booking.meeting_url || booking.location_details || 'Online'}`,
            `UID:${booking.id}@photonx`, 'END:VEVENT', 'END:VCALENDAR',
        ].join('\r\n');
        const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
        const a = document.createElement('a');
        a.href = url; a.download = `${eventType.slug}-${booking.id}.ics`;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
            </div>
        );
    }

    if (!booking || !eventType) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-2">Booking Not Found</h2>
                    <p className="text-muted-foreground">This booking doesn't exist or has been removed.</p>
                </div>
            </div>
        );
    }

    if (isAwaitingPayment) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center max-w-md" data-testid="payment-confirming">
                    {!pollTimedOut ? (
                        <>
                            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent mx-auto mb-5" />
                            <h2 className="text-2xl font-bold mb-2">Confirming your payment…</h2>
                            <p className="text-muted-foreground">
                                This usually takes a few seconds. Please don't close this page.
                            </p>
                        </>
                    ) : (
                        <>
                            <h2 className="text-2xl font-bold mb-2">Payment is being processed</h2>
                            <p className="text-muted-foreground">
                                This is taking longer than usual. We're still checking — this page will
                                update on its own the moment your payment is confirmed, and the
                                confirmation email (with the joining details) follows straight after.
                                If you didn't complete the payment, you can simply register again —
                                this attempt won't hold a seat.
                            </p>
                            <Button
                                variant="outline"
                                className="mt-5"
                                onClick={handleCheckNow}
                                disabled={checkingNow}
                                data-testid="payment-check-now"
                            >
                                {checkingNow ? 'Checking…' : 'Check again'}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    const start = new Date(booking.start_time);
    const end = new Date(booking.end_time);
    const timezone = booking.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    const LOCATION_LABELS: Record<string, string> = {
        google_meet: 'Google Meet',
        zoom: 'Zoom',
        teams: 'Microsoft Teams',
        phone: 'Phone call',
        in_person: 'In person',
        custom: 'Web conference',
    };
    const providerLabel = LOCATION_LABELS[booking.location_type || ''] || 'Web conference';
    const isVideoLocation = !['phone', 'in_person'].includes(booking.location_type || '');

    const timeStr = `${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} – ${end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    const dateStr = start.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

    return (
        <div className={`min-h-screen flex items-center justify-center py-10 px-4 ${isDark ? 'dark bg-[#0b1120]' : 'bg-muted/20'}`}>
            <div className="w-full max-w-[520px]">

                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-5">
                        <Check className="h-8 w-8 text-green-600 dark:text-green-400 stroke-[2.5]" />
                    </div>
                    <h1 className="text-2xl font-bold mb-1">You're Scheduled!</h1>
                    <p className="text-sm text-muted-foreground">
                        A confirmation email has been sent to{' '}
                        <span className="font-medium text-foreground">{booking.invitee_email}</span>
                    </p>
                </div>

                <div className="bg-background rounded-2xl border shadow-sm overflow-hidden mb-4">
                    <div className="px-6 py-5 border-b">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Booking Details</p>
                        <h2 className="text-lg font-bold">{eventType.name}</h2>
                    </div>

                    <div className="px-6 py-5 space-y-4">
                        <div className="flex items-start gap-3">
                            <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            <div>
                                <p className="text-sm font-semibold">{dateStr}</p>
                                <p className="text-sm text-muted-foreground">{timeStr}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                            <p className="text-sm text-muted-foreground">
                                {eventType.duration} minutes · {timezone}
                            </p>
                        </div>

                        <div className="flex items-start gap-3">
                            <Mail className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            <div>
                                <p className="text-sm font-semibold">{booking.invitee_name}</p>
                                <p className="text-sm text-muted-foreground">{booking.invitee_email}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            {booking.location_type === 'phone' ? (
                                <Phone className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            ) : booking.location_type === 'in_person' ? (
                                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            ) : (
                                <Video className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            )}
                            <div className="space-y-2">
                                <p className="text-sm font-semibold">{providerLabel}</p>
                                {booking.meeting_url ? (
                                    <div className="flex flex-wrap items-center gap-3">
                                        <a
                                            href={booking.meeting_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-input text-sm font-medium hover:bg-accent transition-colors"
                                        >
                                            Join {providerLabel}
                                        </a>
                                        <span className="text-xs text-muted-foreground break-all">{booking.meeting_url}</span>
                                    </div>
                                ) : booking.location_details ? (
                                    <p className="text-sm text-muted-foreground">{booking.location_details}</p>
                                ) : isVideoLocation ? (
                                    <p className="text-sm text-muted-foreground">
                                        The joining link will be shared with you by email before the meeting.
                                    </p>
                                ) : (
                                    <p className="text-sm text-muted-foreground">Details in your confirmation email</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>


                <div className="text-center space-y-3">
                    <Link
                        to={`/book/${eventType.slug}`}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
                    >
                        Schedule another meeting
                    </Link>
                    <p className="text-xs text-muted-foreground">
                        © 2025 WeNext. Powered by PhotonX
                    </p>
                </div>
            </div>
        </div>
    );
}
